import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LastMinuteInviteManager from '../components/LastMinuteInviteManager';
import LastMinuteSpotViewer from '../components/LastMinuteSpotViewer';

const LastMinuteInviteTest = () => {
  const [activeTab, setActiveTab] = useState('manager');
  const [testTournamentId] = useState('688e9fd8d30522395764f326'); // Old Guy Prep tournament
  const { user, isTournamentDirector } = useAuth();

  const renderTabButton = (tabKey, label, icon) => (
    <button
      key={tabKey}
      onClick={() => setActiveTab(tabKey)}
      className={`tab-button ${activeTab === tabKey ? 'active' : ''}`}
    >
      {icon} {label}
    </button>
  );

  if (!user) {
    return (
      <div className="last-minute-test">
        <div className="auth-required">
          <h2>Authentication Required</h2>
          <p>Please log in to test the last-minute invite system.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="last-minute-test">
      <div className="test-header">
        <h1>🧪 Last-Minute Invite System Test</h1>
        <p>Testing environment for the new last-minute invite functionality</p>
        
        <div className="user-info">
          <strong>Logged in as:</strong> {user.fullName} ({user.role})
        </div>
      </div>

      <div className="test-tabs">
        {isTournamentDirector() && renderTabButton('manager', 'TD Management', '🔧')}
        {renderTabButton('viewer', 'Member View', '👤')}
        {renderTabButton('preferences', 'Test Preferences', '⚙️')}
      </div>

      <div className="test-content">
        {activeTab === 'manager' && isTournamentDirector() && (
          <div className="test-section">
            <h2>Tournament Director Management Interface</h2>
            <p>Create spots, view reservations, and manage the last-minute invite process</p>
            <LastMinuteInviteManager 
              tournamentId={testTournamentId}
              tournamentName="Old Guy Prep"
            />
          </div>
        )}

        {activeTab === 'viewer' && (
          <div className="test-section">
            <h2>Member Spot Viewing Interface</h2>
            <p>This is what members see when they click the email link</p>
            <LastMinuteSpotViewer 
              tournamentId={testTournamentId}
              isPublic={false}
            />
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="test-section">
            <h2>Member Preferences Test</h2>
            <p>Test the member preference system</p>
            
            <div className="preference-test">
              <div className="test-info">
                <h3>How to Test:</h3>
                <ol>
                  <li>Go to a member's profile page (like Mike Anderson)</li>
                  <li>Click "Edit" to modify their preferences</li>
                  <li>Toggle "Accept Last-Minute Invites" checkbox</li>
                  <li>Set notification preferences</li>
                  <li>Save the changes</li>
                  <li>Come back here to test the spot creation workflow</li>
                </ol>
              </div>
              
              <div className="quick-links">
                <h4>Quick Test Links:</h4>
                <a href="/members/6891003640650a77ceb3662c" target="_blank" rel="noopener noreferrer">
                  📝 Edit Mike Anderson's Preferences
                </a>
                <a href="/members/6891003640650a77ceb36623" target="_blank" rel="noopener noreferrer">
                  📝 Edit Mike Aiken's Preferences  
                </a>
                <a href="/members/6891003640650a77ceb36629" target="_blank" rel="noopener noreferrer">
                  📝 Edit Dan Allen's Preferences
                </a>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'manager' && !isTournamentDirector() && (
          <div className="test-section">
            <div className="access-denied">
              <h3>Tournament Director Access Required</h3>
              <p>Only tournament directors can access the management interface.</p>
              <p>You are currently logged in as: <strong>{user.role}</strong></p>
            </div>
          </div>
        )}
      </div>

      <div className="test-footer">
        <div className="test-status">
          <h3>🎯 Test Progress</h3>
          <ul>
            <li>✅ Database models created</li>
            <li>✅ API endpoints implemented</li>
            <li>✅ Frontend components built</li>
            <li>🔄 Testing workflow...</li>
            <li>⏳ Email integration (next phase)</li>
          </ul>
        </div>
      </div>

      <style jsx>{`
        .last-minute-test {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .test-header {
          text-align: center;
          margin-bottom: 32px;
          padding-bottom: 20px;
          border-bottom: 2px solid #e0e0e0;
        }

        .test-header h1 {
          color: #333;
          margin: 0 0 8px 0;
          font-size: 28px;
        }

        .test-header p {
          color: #666;
          margin: 0 0 16px 0;
          font-size: 16px;
        }

        .user-info {
          background: #e3f2fd;
          color: #1976d2;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          display: inline-block;
        }

        .test-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          border-bottom: 1px solid #e0e0e0;
          padding-bottom: 16px;
        }

        .tab-button {
          background: #f5f5f5;
          color: #666;
          border: 1px solid #ddd;
          padding: 10px 16px;
          border-radius: 6px 6px 0 0;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .tab-button:hover {
          background: #e3f2fd;
          color: #1976d2;
        }

        .tab-button.active {
          background: #4CAF50;
          color: white;
          border-color: #4CAF50;
        }

        .test-content {
          min-height: 400px;
        }

        .test-section h2 {
          color: #333;
          margin: 0 0 8px 0;
        }

        .test-section > p {
          color: #666;
          margin: 0 0 24px 0;
        }

        .auth-required {
          text-align: center;
          padding: 60px 20px;
        }

        .access-denied {
          text-align: center;
          padding: 40px 20px;
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 8px;
        }

        .preference-test {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 24px;
        }

        .test-info h3 {
          color: #333;
          margin-bottom: 12px;
        }

        .test-info ol {
          color: #666;
          line-height: 1.6;
        }

        .quick-links {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #f0f0f0;
        }

        .quick-links h4 {
          color: #333;
          margin-bottom: 12px;
        }

        .quick-links a {
          display: block;
          color: #1976d2;
          text-decoration: none;
          padding: 8px 0;
          font-size: 14px;
        }

        .quick-links a:hover {
          text-decoration: underline;
        }

        .test-footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e0e0e0;
        }

        .test-status {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
        }

        .test-status h3 {
          color: #333;
          margin: 0 0 12px 0;
        }

        .test-status ul {
          margin: 0;
          color: #666;
        }

        .test-status li {
          margin-bottom: 4px;
        }
      `}</style>
    </div>
  );
};

export default LastMinuteInviteTest;
